import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Hook to fetch the current user's accessible areas (sales / invoice) + roles.
 * Returns { loading, areas, roles, hasAccess, error }.
 *
 * Cached in module scope so multiple components can share one fetch per session.
 */
let cached = null;
let inflight = null;

export function clearAccessCache() {
  cached = null;
  inflight = null;
}

async function fetchAccess() {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = api
    .get("/auth/access")
    .then((r) => {
      cached = r.data;
      inflight = null;
      return cached;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function useAccess() {
  const [state, setState] = useState({
    loading: true,
    areas: [],
    roles: [],
    hasAccess: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAccess();
        if (cancelled) return;

        setState({
          loading: false,
          areas: data.areas || [],
          roles: data.roles || [],
          hasAccess: !!data.has_access,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          areas: [],
          roles: [],
          hasAccess: false,
          error: err?.response?.data?.detail || "Failed to verify access",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** True if the current URL is under the given area prefix. */
export function pathIsArea(pathname, area) {
  if (area === "sales") return pathname.startsWith("/sales");
  if (area === "inventory") return pathname.startsWith("/inventory");
  if (area === "invoice") {
    return (
      pathname.startsWith("/invoices") ||
      pathname.startsWith("/products") ||
      pathname.startsWith("/settings")
    );
  }
  return false;
}
